import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/anton/latin-400.css";
import "@fontsource/barlow-condensed/latin-800.css";
import "@fontsource/bricolage-grotesque/latin-600.css";
import "@fontsource/bricolage-grotesque/latin-800.css";
import "@fontsource-variable/bricolage-grotesque/wdth.css";
import "@fontsource/fredoka/latin-600.css";
import "@fontsource/fredoka/latin-700.css";
import "./app.css";
import "./motion.css";
import { exactShort } from "../../shared/short";
import { LinkStopped } from "./components/LinkStopped";
import { screenFromLocation } from "./lib/useTournament";
import { AdminView } from "./views/Admin";
import { CalibrationView } from "./views/Calibration";
import { CardView } from "./views/Card";
import { CourtView } from "./views/Court";
import { CourtsAllView } from "./views/CourtsAll";
import { Landing } from "./views/Landing";
import { PlayerView } from "./views/Player";
import { PrintView } from "./views/Print";
import { ProjectorView } from "./views/Projector";
import { ResultsView } from "./views/Results";
// last, so it wins over every view's own stylesheet
import "./blocks.css";

function App() {
  if (isResults(location.pathname)) return <ResultsView />;
  const screen = screenFromLocation();
  // a court tablet, the wall or a phone whose link has stopped says so over everything (not the
  // admin's screen, which has its own panel for it)
  return (
    <>
      <View />
      {screen && screen.role !== "admin" && <LinkStopped role={screen.role} />}
    </>
  );
}

function View() {
  // the results after the day (decision 54): no tournament, no link
  if (isResults(location.pathname)) return <ResultsView />;
  // the card for the group chat: no tournament, no link
  if (location.pathname.replace(/\/+$/, "") === "/card") return <CardView />;
  // the four court tablets on one screen, for a test run: /courts?t=...#tok1,tok2,tok3,tok4 (the setup page has it)
  if (location.pathname.replace(/\/+$/, "") === "/courts") return <CourtsAllView tokens={location.hash.slice(1).split(",").filter(Boolean)} />;
  const screen = screenFromLocation();
  if (!screen) return <Landing />;
  const third = location.pathname.split("/").filter(Boolean)[2];
  const calibration = third === "cal";
  switch (screen.role) {
    case "projector":
      return calibration ? <CalibrationView screen={screen} /> : <ProjectorView screen={screen} />;
    case "court":
      return <CourtView screen={screen} />;
    case "admin":
      return third === "print" ? <PrintView screen={screen} /> : <AdminView screen={screen} />;
    case "player":
      return <PlayerView screen={screen} />;
    default:
      return <Landing />;
  }
}

// A short address typed with capitals or a slash at the end (/Wall, /court1/) never reaches the
// Worker and used to show the title page; /courts without its # used to be an empty page. Both go
// on to the exact short address, which the Worker sends to the right screen (decision 51).
function exactAddress(): string | null {
  const fixed = exactShort(location.pathname);
  if (fixed) return fixed + location.search + location.hash;
  const path = location.pathname.replace(/\/+$/, "");
  if ((path === "/courts" || path === "/practice/courts") && !location.hash.slice(1)) {
    const q = new URLSearchParams(location.search);
    if (q.get("mock") === "1") return null;
    return path === "/practice/courts" || q.get("t") === "rehearsal" ? "/practice/courts" : "/courts";
  }
  return null;
}
function isResults(path: string): boolean {
  return /^\/results(\/|$)/.test(path);
}

// After the day (decision 54) the production build sends every screen but the admin's (which still
// opens, and can change nothing) and the card to the results. The Worker does the same for the
// short addresses before any of this loads. Every production build does it (VITE_DAY_OVER=0 at
// build time undoes it); the dev server and the tests never do.
function afterTheDay(): string | null {
  if (!import.meta.env.PROD || import.meta.env.VITE_DAY_OVER === "0") return null;
  const path = location.pathname;
  if (isResults(path) || path.startsWith("/a/") || path.replace(/\/+$/, "") === "/card") return null;
  return "/results";
}
const goTo = afterTheDay() ?? exactAddress();
if (goTo) location.replace(goTo);

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
